import { z } from "zod";
import { SOLVE_PHASES } from "@playbookai/types";
import { CountermeasureStatus, idString, IsoDate, OrdinalBand, ShortText, } from "./common.js";
import { CONTAINMENT_ACTION_STATUSES, CONTAINMENT_EFFECTIVENESS, CONTAINMENT_PRIORITIES, } from "./containmentPlan.js";
/**
 * SOLVE Actions — a cross-SOLVE, READ-ONLY projection of the two action-bearing
 * SOLVE canvas widgets onto Personal Home.
 *
 * The two sources keep their own vocabularies on purpose. A Containment Plan
 * action has a structured `eta` and can therefore be overdue; a Countermeasure
 * Table row has a free-text implementation `window` and can NOT — parsing it
 * would invent a date the author never wrote. Every field below is either
 * carried verbatim from the source or derived by a rule stated here, and
 * nothing on this contract is writable: editing stays on the canvas.
 */
// ---------- filter vocabularies (connection parameters) ----------
export const SOLVE_ACTION_SOURCES = ["containment", "countermeasure"];
export const SOLVE_ACTION_OWNER_FILTERS = [
    "any",
    "me",
    "selected",
    "unassigned",
];
/**
 * Due filters apply to CONTAINMENT rows only, and the payload says so rather
 * than quietly dropping countermeasures. `no_due_date` is a real question a
 * containment reader asks ("what did we never schedule?") and is not the same
 * as `any`.
 */
export const SOLVE_ACTION_DUE_FILTERS = [
    "any",
    "overdue",
    "next_7_days",
    "next_30_days",
    "no_due_date",
];
/** Presentation grouping. `none` is a flat, globally ordered list. */
export const SOLVE_ACTION_GROUPINGS = [
    "none",
    "solve",
    "source",
    "due",
];
/**
 * Optional columns. Action, owner, status, timing and source SOLVE are the
 * compact baseline and are always rendered, so they are deliberately NOT
 * selectable here — a row with no action text is not a row.
 */
export const SOLVE_ACTION_OPTIONAL_COLUMNS = [
    "priority",
    "impactEffort",
    "effectiveness",
];
/** Server-side scope bound: how many accessible SOLVEs one resolve may scan. */
export const SOLVE_ACTIONS_MAX_SOURCE_SOLVES = 40;
/** Rows returned by one resolve, after ordering. */
export const SOLVE_ACTIONS_MAX_ROWS = 50;
/** Owner values offered by the editor. */
export const SOLVE_ACTIONS_MAX_OWNER_OPTIONS = 50;
/** Explicit SOLVE selections one instance may persist. */
export const SOLVE_ACTIONS_MAX_SELECTED_SOLVES = 25;
/** Explicit owner selections one instance may persist. */
export const SOLVE_ACTIONS_MAX_SELECTED_OWNERS = 25;
/**
 * The ONE owner-matching rule: trim surrounding whitespace, compare
 * case-insensitively. Exported so the provider's filter, the provider's option
 * derivation and the editor's de-duplication cannot drift apart.
 *
 * Owner text is NOT a user account. Nothing here resolves a name to a user, and
 * no caller may infer "assigned to me" from it.
 */
export function normalizeSolveActionOwner(value) {
    return value.trim().toLowerCase();
}
// ---------- presentation params (widget instance) ----------
export const SolveActionsParamsSchema = z
    .object({
    groupBy: z.enum(SOLVE_ACTION_GROUPINGS),
    columns: z
        .array(z.enum(SOLVE_ACTION_OPTIONAL_COLUMNS))
        .max(SOLVE_ACTION_OPTIONAL_COLUMNS.length),
})
    .strict();
export const defaultSolveActionsParams = {
    groupBy: "solve",
    columns: [],
};
// ---------- dataset input (connection parameters) ----------
/**
 * Original fields remain REQUIRED; optional account filters replace legacy
 * names only when an explicit `owner` mode is supplied.
 *
 * - `solveIds: []` → every SOLVE the actor can currently see (revalidated per
 *   resolve, never a frozen list).
 * - `owners: []` → every owner, INCLUDING rows with no owner text at all.
 *   Selecting any owner therefore hides unowned rows, which is what asking for
 *   a named owner means.
 *
 * The status axes are per source and carry the source's own labels, so a
 * selection can never be read against the wrong vocabulary. `.min(1)` on each
 * makes "do not narrow" spell itself as "all of them" — the same convention
 * `kaizenPlanStatusFilterNarrows` relies on.
 */
export const SolveActionsInputSchema = z
    .object({
    sources: z.array(z.enum(SOLVE_ACTION_SOURCES)).min(1).max(2),
    solveIds: z.array(idString).max(SOLVE_ACTIONS_MAX_SELECTED_SOLVES),
    owners: z.array(ShortText.min(1)).max(SOLVE_ACTIONS_MAX_SELECTED_OWNERS),
    owner: z.enum(SOLVE_ACTION_OWNER_FILTERS).optional(),
    ownerIds: z
        .array(z.string().uuid())
        .max(SOLVE_ACTIONS_MAX_SELECTED_OWNERS)
        .optional(),
    containmentStatuses: z
        .array(z.enum(CONTAINMENT_ACTION_STATUSES))
        .min(1)
        .max(CONTAINMENT_ACTION_STATUSES.length),
    countermeasureStatuses: z
        .array(CountermeasureStatus)
        .min(1)
        .max(CountermeasureStatus.options.length),
    dueState: z.enum(SOLVE_ACTION_DUE_FILTERS),
})
    .strict()
    .superRefine((input, context) => {
    if (input.owner === undefined) {
        if (input.ownerIds !== undefined)
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["ownerIds"],
                message: "Choose an account owner mode before selecting people.",
            });
        return;
    }
    if (input.owners.length > 0)
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["owners"],
            message: "Account owner filters replace saved owner names.",
        });
    if (input.owner === "selected" && !input.ownerIds?.length)
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["ownerIds"],
            message: "Choose at least one owner.",
        });
    if (input.owner !== "selected" && input.ownerIds?.length)
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["ownerIds"],
            message: "Specific people require the selected owner mode.",
        });
});
/**
 * The default an auto-bound connection starts from: both sources, every
 * accessible SOLVE, every owner, and the UNFINISHED statuses of each source.
 *
 * Defaulting to unfinished rather than to everything is the one opinionated
 * choice, and it matches what the two widgets are for — a Done containment
 * action and a Done countermeasure are history, and a Home widget that opens
 * full of completed work buries the work that is live. Both are one checkbox
 * away in the editor.
 */
export const DEFAULT_SOLVE_ACTIONS_INPUT = {
    sources: [...SOLVE_ACTION_SOURCES],
    solveIds: [],
    owners: [],
    containmentStatuses: ["Open", "In progress"],
    countermeasureStatuses: ["Proposed", "Approved", "In Design", "Trial"],
    dueState: "any",
};
// ---------- dataset output ----------
/**
 * One projected action.
 *
 * `id` is `<source>:<widgetId>:<itemId>` — stable across SOLVEs because the
 * widget id is a uuid and the item id is unique inside its widget. It is the
 * React key and the navigation subject, never an address anything writes to.
 *
 * The nullable fields are nullable because the OTHER source does not have them,
 * not because the value is missing: `due`/`overdue` are containment-only,
 * `windowText` is countermeasure-only, `priority`/`effectiveness` are
 * containment-only and `impact`/`effort` are countermeasure-only. A consumer
 * must render an absent field as absent and never as a zero or a default.
 */
export const SolveActionRowSchema = z
    .object({
    id: z.string().min(1).max(200),
    source: z.enum(SOLVE_ACTION_SOURCES),
    solveId: idString,
    solveTitle: ShortText,
    /** The originating `widget.widgets` row — the canvas navigation subject. */
    widgetId: idString,
    /** The action/row id INSIDE that widget's params. */
    itemId: idString,
    /** The originating widget's placement phase, when it has one. */
    phase: z.enum(SOLVE_PHASES).nullable(),
    action: ShortText,
    /** Verbatim owner text; `""` when the source field is blank. */
    owner: ShortText,
    /** The source's own status label, verbatim. */
    status: ShortText,
    /** Containment only: the structured ETA. Null for a countermeasure. */
    due: IsoDate.nullable(),
    /**
     * Containment only, computed server-side by `isContainmentOverdue` so the
     * badge and the `overdue` filter cannot disagree. ALWAYS false for a
     * countermeasure — its window is prose, and a manufactured overdue on it
     * would be a claim nobody made.
     */
    overdue: z.boolean(),
    /** Countermeasure only: the implementation window, verbatim, never parsed. */
    windowText: ShortText.nullable(),
    priority: z.enum(CONTAINMENT_PRIORITIES).nullable(),
    effectiveness: z.enum(CONTAINMENT_EFFECTIVENESS).nullable(),
    impact: OrdinalBand.nullable(),
    effort: OrdinalBand.nullable(),
})
    .strict();
export const SolveActionsOutputSchema = z
    .object({
    rows: z.array(SolveActionRowSchema).max(SOLVE_ACTIONS_MAX_ROWS),
    /** More rows matched the filter than `rows` carries (ordering ran first). */
    truncated: z.boolean(),
    /** Matches found, itself capped — a count must not be unbounded either. */
    matchedCount: z.number().int().min(0),
    /**
     * Owner values present in the SAVED source + SOLVE scope, before the owner,
     * status and due filters narrow anything. Normalised for comparison but
     * carried in their first-seen display casing, so the editor offers what the
     * author actually typed.
     */
    ownerOptions: z
        .array(ShortText.min(1))
        .max(SOLVE_ACTIONS_MAX_OWNER_OPTIONS),
    /** Selected SOLVEs the actor can no longer see — reported, never ignored. */
    unavailableSolveIds: z
        .array(idString)
        .max(SOLVE_ACTIONS_MAX_SELECTED_SOLVES),
    /** A due filter is active, so countermeasure rows were left out entirely. */
    dueFilterExcludesCountermeasures: z.boolean(),
    /** More accessible SOLVEs exist than one resolve may scan. */
    scopeTruncated: z.boolean(),
})
    .strict();
// ---------- shared derivations ----------
/** Does this status selection actually exclude anything? */
export function solveActionStatusFilterNarrows(selected, all) {
    return new Set(selected).size < all.length;
}
export function solveActionDueBucket(row, today) {
    if (row.source === "countermeasure")
        return "unscheduled";
    if (row.overdue)
        return "overdue";
    if (row.due === null || row.due === "")
        return "no_due_date";
    if (row.due <= addIsoDays(today, 7))
        return "next_7_days";
    if (row.due <= addIsoDays(today, 30))
        return "next_30_days";
    return "later";
}
/** `YYYY-MM-DD` + n days, in UTC, so the arithmetic never crosses a DST seam. */
export function addIsoDays(isoDate, days) {
    const [year, month, day] = isoDate.split("-").map(Number);
    if (year === undefined || month === undefined || day === undefined) {
        return isoDate;
    }
    const shifted = new Date(Date.UTC(year, month - 1, day + days));
    return shifted.toISOString().slice(0, 10);
}
export const SOLVE_ACTION_DUE_BUCKET_LABELS = {
    overdue: "Overdue",
    next_7_days: "Next 7 days",
    next_30_days: "Next 30 days",
    later: "Later",
    no_due_date: "No due date",
    unscheduled: "No structured date",
};
export const SOLVE_ACTION_SOURCE_LABELS = {
    containment: "Containment Plan",
    countermeasure: "Countermeasure Table",
};
//# sourceMappingURL=solveActions.js.map